import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authAPI, connectionsAPI } from '../api/client';
import { Search, Smartphone, Link, Send, Mail, ChevronDown, ChevronUp } from 'lucide-react';
import Layout from '../components/Layout';
import '../styles/find-friends.css';

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return (parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : name[0]).toUpperCase();
}

function UserRow({ user, sent, connStatus = {}, connIdMap = {}, onAdd, onAccept, onDecline }) {
  const status = connStatus[user.id];
  const isSent = sent.has(user.id);
  return (
    <div className="ff-user-row">
      <div className="ff-avatar">{getInitials(user.display_name)}</div>
      <div className="ff-user-info">
        <span className="ff-user-name">{user.display_name || 'Unnamed'}</span>
        {(user.instagram_handle || user.twitter_handle) && (
          <span className="ff-user-handles">
            {user.instagram_handle && <span>@{user.instagram_handle}</span>}
            {user.twitter_handle && <span>@{user.twitter_handle}</span>}
          </span>
        )}
      </div>
      {status === 'accepted' ? (
        <span className="ff-friends-badge">Friends ✓</span>
      ) : status === 'pending_sent' || isSent ? (
        <span className="ff-sent-badge">Requested</span>
      ) : status === 'pending_received' ? (
        <div className="ff-action-btns">
          <button className="ff-accept-btn" onClick={() => onAccept?.(connIdMap[user.id])}>Accept</button>
          <button className="ff-decline-link" onClick={() => onDecline?.(connIdMap[user.id])}>Decline</button>
        </div>
      ) : (
        <button className="ff-add-btn" onClick={() => onAdd(user.id)}>Add</button>
      )}
    </div>
  );
}

export default function FindFriends() {
  const [sent, setSent]           = useState(new Set());
  const [searchParams]            = useSearchParams();
  const [toast, setToast]         = useState('');
  const [connStatus, setConnStatus] = useState({});
  const [connIdMap, setConnIdMap]   = useState({});
  const currentUserId = localStorage.getItem('userId');

  useEffect(() => {
    const ig = searchParams.get('instagram');
    const err = searchParams.get('error');
    const handle = searchParams.get('handle');
    if (ig === 'connected') setToast(`Instagram connected${handle ? ` as @${handle}` : ''}!`);
    else if (err === 'instagram_denied') setToast('Instagram connection cancelled.');
    else if (err) setToast('Instagram connection failed. Try again.');
    if (ig || err) setTimeout(() => setToast(''), 4000);
  }, [searchParams]);

  const loadConnections = async () => {
    try {
      const res = await connectionsAPI.getAll();
      const statusMap = {};
      const idMap = {};
      (res.data || []).forEach(c => {
        const otherId = c.user_id === currentUserId ? c.connected_user_id : c.user_id;
        if (c.status === 'accepted') statusMap[otherId] = 'accepted';
        else if (c.status === 'pending')
          statusMap[otherId] = c.user_id === currentUserId ? 'pending_sent' : 'pending_received';
        idMap[otherId] = c.id;
      });
      setConnStatus(statusMap);
      setConnIdMap(idMap);
    } catch {}
  };

  useEffect(() => { loadConnections(); }, []);

  const handleAdd = async (userId) => {
    try {
      await connectionsAPI.add(userId);
      setSent(prev => new Set([...prev, userId]));
    } catch {}
  };

  const handleAccept = async (connId) => {
    try { await connectionsAPI.accept(connId); loadConnections(); } catch {}
  };

  const handleDecline = async (connId) => {
    try { await connectionsAPI.delete(connId); loadConnections(); } catch {}
  };

  const sharedProps = { sent, connStatus, connIdMap, onAdd: handleAdd, onAccept: handleAccept, onDecline: handleDecline };

  return (
    <Layout title="Find Friends">
      <div className="ff-page">
        {toast && <div className="ff-toast">{toast}</div>}

        <div className="ff-header">
          <h1 className="ff-title">Add Friends</h1>
          <p className="ff-sub">Find people you know on Covo... or add your friends to Covo!</p>
        </div>

        <PhoneContactsSection {...sharedProps} />
        <SearchSection {...sharedProps} />
        <InviteSection />
      </div>
    </Layout>
  );
}

/* ── Section 0: Instagram ────────────────────────────────── */
function InstagramSection({ sent, connStatus, connIdMap, onAdd, onAccept, onDecline }) {
  const [expanded, setExpanded]   = useState(true);
  const [me, setMe]               = useState(null);
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [disconnecting, setDisc]  = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [meRes, usersRes] = await Promise.all([
        authAPI.getMe(),
        instagramAPI.getUsers(),
      ]);
      setMe(meRes.data);
      setUsers(usersRes.data || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleConnect = () => {
    window.location.href = '/api/instagram/auth';
  };

  const handleDisconnect = async () => {
    setDisc(true);
    try { await instagramAPI.disconnect(); await load(); } catch {}
    setDisc(false);
  };

  const isConnected = me?.instagram_connected;
  const handle = me?.instagram_handle;

  return (
    <div className="ff-section">
      <button className="ff-section-toggle" onClick={() => setExpanded(v => !v)}>
        <div className="ff-section-toggle-left">
          <div className="ff-section-icon" style={{ background: 'linear-gradient(135deg,#f9ce34,#ee2a7b,#6228d7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </div>
          <div>
            <span className="ff-section-label">Instagram</span>
            <span className="ff-section-desc">
              {isConnected ? `Connected as @${handle}` : 'Find your Instagram friends on Covo'}
            </span>
          </div>
        </div>
        {expanded ? <ChevronUp size={16} color="#888" /> : <ChevronDown size={16} color="#888" />}
      </button>

      {expanded && (
        <div className="ff-section-body">
          {/* Connect / status bar */}
          <div className="ff-ig-status">
            {isConnected ? (
              <div className="ff-ig-connected">
                <span className="ff-ig-verified">✓ @{handle} verified</span>
                <button className="ff-ig-disconnect" onClick={handleDisconnect} disabled={disconnecting}>
                  {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                </button>
              </div>
            ) : (
              <button className="ff-ig-connect-btn" onClick={handleConnect}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white" style={{ flexShrink: 0 }}>
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                Connect Instagram
              </button>
            )}
          </div>

          {/* Users on Covo with Instagram */}
          {loading ? (
            <p className="ff-scanning">Loading…</p>
          ) : users.length === 0 ? (
            <p className="ff-no-results">No other Covo users have linked their Instagram yet.</p>
          ) : (
            <div className="ff-match-group">
              <p className="ff-match-label">Covo users with Instagram ({users.length})</p>
              {users.map(u => (
                <UserRow key={u.id} user={u} sent={sent} connStatus={connStatus} connIdMap={connIdMap} onAdd={onAdd} onAccept={onAccept} onDecline={onDecline} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Section 1: Phone contacts ───────────────────────────── */
function PhoneContactsSection({ sent, connStatus, connIdMap, onAdd, onAccept, onDecline }) {
  const [status, setStatus] = useState('idle'); // idle | scanning | done | unsupported
  const [matched, setMatched] = useState([]);
  const [unmatched, setUnmatched] = useState([]); // raw names+phones for SMS invite
  const [expanded, setExpanded] = useState(true);
  const contactsSupported = typeof navigator !== 'undefined' && 'contacts' in navigator && 'ContactsManager' in window;

  const handleImport = async () => {
    setStatus('scanning');
    try {
      const raw = await navigator.contacts.select(['name', 'email', 'tel'], { multiple: true });
      const payload = raw.map(c => ({ emails: c.email || [], phones: c.tel || [] }));
      const res = await connectionsAPI.findByContacts(payload);
      setMatched(res.data.matched || []);
      // Build unmatched list for invite-by-SMS
      const matchedEmails = new Set((res.data.matched || []).map(u => u.phone_email));
      const unmatchedContacts = raw.filter(c =>
        !c.email?.some(e => matchedEmails.has(e)) &&
        (c.tel?.length > 0 || c.email?.length > 0)
      ).slice(0, 20);
      setUnmatched(unmatchedContacts);
      setStatus('done');
    } catch (err) {
      if (err.name !== 'AbortError') setStatus('idle');
      else setStatus('idle');
    }
  };

  const inviteLink = `${window.location.origin}/register`;

  const smsHref = (contact) => {
    const phone = contact.tel?.[0] || '';
    const name = contact.name?.[0] || 'someone';
    const body = encodeURIComponent(`Hey ${name}! Join me on Covo to coordinate plans: ${inviteLink}`);
    return `sms:${phone}?body=${body}`;
  };

  return (
    <div className="ff-section">
      <button className="ff-section-toggle" onClick={() => setExpanded(v => !v)}>
        <div className="ff-section-toggle-left">
          <div className="ff-section-icon" style={{ background: '#e8f5e9' }}>
            <Smartphone size={18} color="#52AAD8" />
          </div>
          <div>
            <span className="ff-section-label">Phone contacts</span>
            <span className="ff-section-desc">Find friends already on Covo</span>
          </div>
        </div>
        {expanded ? <ChevronUp size={16} color="#888" /> : <ChevronDown size={16} color="#888" />}
      </button>

      {expanded && (
        <div className="ff-section-body">
          {!contactsSupported ? (
            <div className="ff-unsupported">
              <p>Phone contact import works on mobile browsers (Safari on iOS, Chrome on Android). Open Covo on your phone to use this feature.</p>
            </div>
          ) : status === 'idle' ? (
            <button className="ff-import-btn" onClick={handleImport}>
              <Smartphone size={16} />
              Import from phone contacts
            </button>
          ) : status === 'scanning' ? (
            <p className="ff-scanning">Scanning contacts…</p>
          ) : (
            <>
              {matched.length === 0 && unmatched.length === 0 ? (
                <p className="ff-no-matches">None of your contacts are on Covo yet. Invite them below!</p>
              ) : null}

              {matched.length > 0 && (
                <div className="ff-match-group">
                  <p className="ff-match-label">On Covo ({matched.length})</p>
                  {matched.map(u => <UserRow key={u.id} user={u} sent={sent} connStatus={connStatus} connIdMap={connIdMap} onAdd={onAdd} onAccept={onAccept} onDecline={onDecline} />)}
                </div>
              )}

              {unmatched.length > 0 && (
                <div className="ff-match-group">
                  <p className="ff-match-label">Not on Covo yet — invite via SMS</p>
                  {unmatched.map((c, i) => (
                    <a key={i} className="ff-sms-row" href={smsHref(c)}>
                      <div className="ff-avatar ff-avatar-grey">{(c.name?.[0] || '?')[0]?.toUpperCase()}</div>
                      <span className="ff-user-name">{c.name?.[0] || 'Unknown'}</span>
                      <span className="ff-sms-badge"><Send size={12} /> Invite</span>
                    </a>
                  ))}
                </div>
              )}

              <button className="ff-rescan-btn" onClick={() => setStatus('idle')}>Scan again</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Section 2: Search ───────────────────────────────────── */
function SearchSection({ sent, connStatus, connIdMap, onAdd, onAccept, onDecline }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await authAPI.search(query.trim());
        setResults(res.data || []);
      } catch { setResults([]); }
      setSearching(false);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  return (
    <div className="ff-section">
      <button className="ff-section-toggle" onClick={() => setExpanded(v => !v)}>
        <div className="ff-section-toggle-left">
          <div className="ff-section-icon" style={{ background: '#eef2ff' }}>
            <Search size={18} color="#52AAD8" />
          </div>
          <div>
            <span className="ff-section-label">Search by name or handle</span>
            <span className="ff-section-desc">Name, email, @instagram, @twitter</span>
          </div>
        </div>
        {expanded ? <ChevronUp size={16} color="#888" /> : <ChevronDown size={16} color="#888" />}
      </button>

      {expanded && (
        <div className="ff-section-body">
          <div className="ff-search-wrap">
            <Search size={15} className="ff-search-icon" />
            <input
              className="ff-search-input"
              placeholder="Search name, email, or @handle…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              autoFocus
            />
            {searching && <span className="ff-spinner">…</span>}
          </div>

          {query.length >= 2 && !searching && results.length === 0 && (
            <p className="ff-no-results">No one found. Try a different name, email, or @handle.</p>
          )}

          {results.map(u => <UserRow key={u.id} user={u} sent={sent} connStatus={connStatus} connIdMap={connIdMap} onAdd={onAdd} onAccept={onAccept} onDecline={onDecline} />)}
        </div>
      )}
    </div>
  );
}

/* ── Section 3: Invite link ──────────────────────────────── */
function InviteSection() {
  const [copied, setCopied] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [emailStatus, setEmailStatus] = useState('idle'); // idle | sending | sent | error
  const [expanded, setExpanded] = useState(false);
  const link = `${window.location.origin}/register`;

  const handleCopy = () => {
    navigator.clipboard.writeText(link).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Join me on Covo', text: 'Plan things together — no noise, no ads.', url: link });
        return;
      } catch {}
    }
    handleCopy();
  };

  const smsLink = () => {
    const phone = phoneInput.replace(/\D/g, '');
    const body = encodeURIComponent(`Hey! Join me on Covo to coordinate plans: ${link}`);
    return `sms:${phone}?body=${body}`;
  };

  const handleEmailInvite = async (e) => {
    e.preventDefault();
    if (!emailInput.includes('@')) return;
    setEmailStatus('sending');
    try {
      await authAPI.inviteByEmail(emailInput.trim());
      setEmailStatus('sent');
      setEmailInput('');
      setTimeout(() => setEmailStatus('idle'), 3000);
    } catch {
      setEmailStatus('error');
      setTimeout(() => setEmailStatus('idle'), 3000);
    }
  };

  return (
    <div className="ff-section">
      <button className="ff-section-toggle" onClick={() => setExpanded(v => !v)}>
        <div className="ff-section-toggle-left">
          <div className="ff-section-icon" style={{ background: '#fdf4e7' }}>
            <Link size={18} color="#d97706" />
          </div>
          <div>
            <span className="ff-section-label">Invite friends</span>
            <span className="ff-section-desc">Share a link or send via text</span>
          </div>
        </div>
        {expanded ? <ChevronUp size={16} color="#888" /> : <ChevronDown size={16} color="#888" />}
      </button>

      {expanded && (
        <div className="ff-section-body">
          {/* Copy link */}
          <div className="ff-invite-link-row">
            <span className="ff-invite-link-text">{link.replace('https://', '')}</span>
            <button className="ff-copy-btn" onClick={handleCopy}>{copied ? '✓ Copied' : 'Copy'}</button>
          </div>

          <button className="ff-share-btn" onClick={handleShare}>
            <Send size={15} /> Share invite link
          </button>

          {/* SMS by phone number */}
          <div className="ff-invite-by-phone">
            <p className="ff-match-label">Or send a text message</p>
            <div className="ff-phone-row">
              <input
                className="ff-phone-input"
                type="tel"
                placeholder="+1 555 000 0000"
                value={phoneInput}
                onChange={e => setPhoneInput(e.target.value)}
              />
              <a
                className={`ff-sms-send-btn${!phoneInput.trim() ? ' disabled' : ''}`}
                href={phoneInput.trim() ? smsLink() : undefined}
                onClick={e => !phoneInput.trim() && e.preventDefault()}
              >
                <Send size={14} /> Text
              </a>
            </div>
          </div>

          {/* Email invite */}
          <div className="ff-invite-by-phone">
            <p className="ff-match-label">Or invite by email</p>
            <form className="ff-phone-row" onSubmit={handleEmailInvite}>
              <input
                className="ff-phone-input"
                type="email"
                placeholder="friend@example.com"
                value={emailInput}
                onChange={e => { setEmailInput(e.target.value); setEmailStatus('idle'); }}
                disabled={emailStatus === 'sending'}
              />
              <button
                type="submit"
                className={`ff-sms-send-btn${!emailInput.includes('@') ? ' disabled' : ''}`}
                disabled={!emailInput.includes('@') || emailStatus === 'sending'}
              >
                {emailStatus === 'sending' ? '…' : emailStatus === 'sent' ? '✓ Sent' : emailStatus === 'error' ? 'Error' : <><Mail size={14} /> Send</>}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
